export type CrmContactProfileSectionTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmContactProfileSectionItem {
  key: string;
  label: string;
  value: string;
  description: string | null;
  href: string | null;
  actionLabel: string | null;
  tone: CrmContactProfileSectionTone;
}

export interface CrmContactProfileSection {
  key: string;
  label: string;
  description: string | null;
  source: "configured" | "custom_fields";
  items: CrmContactProfileSectionItem[];
}

export interface CrmContactProfileSectionsResult {
  sections: CrmContactProfileSection[];
  remainingFields: CrmContactProfileSectionItem[];
}

export interface BuildCrmContactProfileSectionsOptions {
  sectionKeys?: readonly string[];
  excludedKeys?: readonly string[];
  maxSections?: number;
  maxItemsPerSection?: number;
}

const DEFAULT_SECTION_KEYS = [
  "crmProfileSections",
  "contactProfileSections",
  "profileSections",
] as const;

const DEFAULT_EXCLUDED_KEYS = [
  "crmProfileSections",
  "contactProfileSections",
  "profileSections",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_VALUE_LENGTH = 160;

export function buildCrmContactProfileSections(
  customFields: unknown,
  options: BuildCrmContactProfileSectionsOptions = {},
): CrmContactProfileSectionsResult {
  const fields = asRecord(customFields);
  if (!fields) return { sections: [], remainingFields: [] };

  const sectionKeys = options.sectionKeys?.length ? [...options.sectionKeys] : [...DEFAULT_SECTION_KEYS];
  const excludedKeys = new Set([
    ...DEFAULT_EXCLUDED_KEYS,
    ...sectionKeys,
    ...(options.excludedKeys ?? []),
  ]);
  const maxSections = normalizePositiveInteger(options.maxSections, 8);
  const maxItemsPerSection = normalizePositiveInteger(options.maxItemsPerSection, 12);

  const configuredSections = sectionKeys.flatMap((key) => parseConfiguredSections(key, fields[key], maxItemsPerSection));
  const remainingFields = Object.entries(fields)
    .filter(([key]) => !excludedKeys.has(key) && !isPrivateKey(key))
    .map(([key, value]) => parseProfileItem(key, value))
    .filter((item): item is CrmContactProfileSectionItem => Boolean(item))
    .slice(0, maxItemsPerSection);

  const sections = configuredSections
    .filter((section) => section.items.length > 0)
    .slice(0, maxSections);

  return { sections, remainingFields };
}

function parseConfiguredSections(
  rootKey: string,
  value: unknown,
  maxItemsPerSection: number,
): CrmContactProfileSection[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseProfileSection(`${rootKey}.${index}`, entry, maxItemsPerSection))
      .filter((section): section is CrmContactProfileSection => Boolean(section));
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasSectionShape(record)) {
    const section = parseProfileSection(rootKey, record, maxItemsPerSection);
    return section ? [section] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parseProfileSection(key, entry, maxItemsPerSection))
    .filter((section): section is CrmContactProfileSection => Boolean(section));
}

function parseProfileSection(
  fallbackKey: string,
  value: unknown,
  maxItemsPerSection: number,
): CrmContactProfileSection | null {
  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? formatProfileLabel(key);
  const description = readString(record.description) ?? readString(record.summary);
  const rawItems = record.items ?? record.fields ?? record.metrics ?? record.signals ?? null;
  const items = parseProfileItems(rawItems ?? record, maxItemsPerSection);

  return {
    key,
    label,
    description,
    source: "configured",
    items,
  };
}

function parseProfileItems(value: unknown, maxItems: number): CrmContactProfileSectionItem[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseProfileItem(`item-${index + 1}`, entry))
      .filter((item): item is CrmContactProfileSectionItem => Boolean(item))
      .slice(0, maxItems);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredSectionKeys = new Set(["key", "label", "title", "description", "summary", "items", "fields", "metrics", "signals"]);
  return Object.entries(record)
    .filter(([key]) => !ignoredSectionKeys.has(key))
    .map(([key, entry]) => parseProfileItem(key, entry))
    .filter((item): item is CrmContactProfileSectionItem => Boolean(item))
    .slice(0, maxItems);
}

function parseProfileItem(fallbackKey: string, value: unknown): CrmContactProfileSectionItem | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = record
    ? readString(record.label) ?? readString(record.title) ?? formatProfileLabel(key)
    : formatProfileLabel(key);
  const description = record ? readString(record.description) ?? readString(record.helpText) : null;
  const href = record ? normalizeHref(readString(record.href) ?? readString(record.url)) : null;
  const actionLabel = record
    ? readString(record.actionLabel) ?? readString(record.ctaLabel) ?? readString(record.linkLabel)
    : null;
  const tone = normalizeTone(record ? readString(record.tone) ?? readString(record.status) : null);
  const rawValue = record && "value" in record ? record.value : value;
  const formattedValue = formatProfileValue(rawValue);

  if (!formattedValue) return null;

  return {
    key,
    label,
    value: formattedValue,
    description,
    href,
    actionLabel,
    tone,
  };
}

function formatProfileValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const primitiveValues = value
      .filter((entry) => ["string", "number", "boolean"].includes(typeof entry))
      .map((entry) => formatProfileValue(entry))
      .filter((entry): entry is string => Boolean(entry));
    if (primitiveValues.length > 0) {
      const preview = primitiveValues.slice(0, 4).join(", ");
      return value.length > 4 ? `${preview}, +${value.length - 4} more` : preview;
    }
    return value.length > 0 ? `${value.length} records` : null;
  }

  const record = asRecord(value);
  if (!record) return null;
  const summary = readString(record.summary) ?? readString(record.label) ?? readString(record.value);
  if (summary) return truncate(summary);
  const visibleKeys = Object.keys(record).filter((key) => !isPrivateKey(key));
  return visibleKeys.length > 0 ? `${visibleKeys.length} fields` : null;
}

function formatProfileLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function hasSectionShape(record: Record<string, unknown>): boolean {
  return ["label", "title", "description", "summary", "items", "fields", "metrics", "signals"].some((key) => key in record);
}

function normalizeTone(value: string | null): CrmContactProfileSectionTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "critical" || value === "error" || value === "failed") return "danger";
  if (value === "ready" || value === "passed" || value === "complete") return "success";
  return "neutral";
}

function normalizeHref(value: string | null): string | null {
  if (!value) return null;
  if (value.startsWith("/") || value.startsWith("https://")) return value;
  return null;
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_VALUE_LENGTH ? `${value.slice(0, MAX_VALUE_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}
